import React, { useMemo, useState } from "react";
import CheckIcon from "../icons/check.icon";
import FilterSectionWithDropdown from "./filterSectionWithDropdown.comp";
// import PriceRangeSlider from "./priceRangeSlider";

interface BrandOption {
  id: string;
  name: string;
  count: number;
  code: string;
}

interface FilterState {
  attrs: { [key: string]: string[] };
  priceRange: { min: number; max: number };
  selectedBrands: string[];
}

// Define the API response format for attribute options
interface ApiAttributeOption {
  attribute_id: string;
  value: string;
  count: number;
}

// Update the interface with the correct types
interface FilterSidebarProps {
  onDiscountChange: (checked: boolean) => void;
  onFilterUpdate: (filters: FilterState) => void;
  brandOptions: BrandOption[];
  attributeOptions: ApiAttributeOption[];
  expanded?: boolean;
}

const ORIGIN_ATTRIBUTE_ID = "64ffdc2ada555709cc24419d";
const STORAGE_ATTRIBUTE_ID = "64ffdbdeda555709cc244195";
const BRAND_ATTRIBUTE_ID = "653f5d2a438484a5a4ec2129";

const defaultOriginOptions = ["烏克蘭", "韓國", "澳洲", "德國", "瑞士", "瑞典", "愛爾蘭", "意大利"];
const defaultStorageOptions = ["冷藏", "冷凍", "常溫"];

export default function FilterSidebar({
  onDiscountChange,
  onFilterUpdate,
  brandOptions,
  attributeOptions,
  expanded = false,
}: FilterSidebarProps): JSX.Element {
  const [isDiscountChecked, setIsDiscountChecked] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    attrs: {
      [ORIGIN_ATTRIBUTE_ID]: [],
      [STORAGE_ATTRIBUTE_ID]: [],
    },
    priceRange: { min: 5, max: 1299 },
    selectedBrands: [],
  });

  const extractValue = (optionWithCount: string) => optionWithCount.split(" (")[0];

  // function handlePriceRangeChange(min: number, max: number): void {
  //   const newFilters = {
  //     ...filters,
  //     priceRange: { min, max },
  //   };

  //   setFilters(newFilters);
  //   onFilterUpdate(newFilters);
  // }

  const handleDiscountCheck = (): void => {
    const newChecked = !isDiscountChecked;
    setIsDiscountChecked(newChecked);
    onDiscountChange(newChecked);
  };

  return (
    <div className="w-full md:w-64 md:flex-shrink-0">
      <div className="border border-red-500 rounded-lg h-[80px] mb-4 md:mb-6 bg-white">
        <label className="h-full flex items-center cursor-pointer px-4 md:px-6">
          <div
            className="w-5 h-5 border border-gray-300 rounded flex-shrink-0 flex items-center justify-center"
            onClick={handleDiscountCheck}
          >
            {isDiscountChecked && <CheckIcon className="w-4 h-4 text-red-500" />}
          </div>
          <div className="flex items-center ml-2 text-red-500">
            <span className="text-lg md:text-xl mr-1">%</span>
            <span className="text-sm md:text-base">Family 優惠價</span>
          </div>
        </label>
      </div>
      <FilterOriginal
        attributeOptions={attributeOptions}
        filters={filters}
        setFilters={setFilters}
        onFilterUpdate={onFilterUpdate}
        extractValue={extractValue}
        expanded={expanded}
      />
      <div className="mt-4">
        <FilterStorage
          attributeOptions={attributeOptions}
          filters={filters}
          setFilters={setFilters}
          onFilterUpdate={onFilterUpdate}
          extractValue={extractValue}
          expanded={expanded}
        />
      </div>

      <div className="mt-4">
        <FilterBrand
          brandOptions={brandOptions}
          filters={filters}
          setFilters={setFilters}
          onFilterUpdate={onFilterUpdate}
          expanded={expanded}
        />
      </div>
      {/* <div className="mt-4">
        <PriceRangeSlider
          min={5}
          max={1299}
          onChange={handlePriceRangeChange}
          initialMin={filters.priceRange.min}
          initialMax={filters.priceRange.max}
        />
      </div> */}
    </div>
  );
}

interface AttributeGroupItem {
  id: string;
  name: string;
  values: { value: string; count: number }[];
}

function useGroupttributeOptions(
  attributeOptions: ApiAttributeOption[]
): { id: string; name: string; values: { value: string; count: number }[] }[] {
  return useMemo(() => {
    if (!attributeOptions || attributeOptions.length === 0) return [];

    // Group by attribute_id
    const groupedByAttrId = attributeOptions.reduce<Record<string, AttributeGroupItem>>((acc, attr) => {
      if (!acc[attr.attribute_id]) {
        acc[attr.attribute_id] = {
          id: attr.attribute_id,
          name: "", // We'll determine this later
          values: [],
        };
      }

      const existing = acc[attr.attribute_id].values.find((v) => v.value === attr.value);
      if (existing) {
        existing.count += attr.count;
      } else {
        acc[attr.attribute_id].values.push({
          value: attr.value,
          count: attr.count,
        });
      }

      return acc;
    }, {});

    return Object.values(groupedByAttrId);
  }, [attributeOptions]);
}

function FilterOriginal({
  attributeOptions,
  filters,
  setFilters,
  onFilterUpdate,
  extractValue,
  expanded,
}: {
  attributeOptions: ApiAttributeOption[];
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  onFilterUpdate: (filters: FilterState) => void;
  extractValue: (optionWithCount: string) => string;
  expanded?: boolean;
}) {
  const groupttributeOptions = useGroupttributeOptions(attributeOptions);
  const originAttribute = groupttributeOptions.find(
    (attr) => attr.id === ORIGIN_ATTRIBUTE_ID // Correct ID from API logs
  );

  if (originAttribute) {
    originAttribute.name = "原產地";
  }

  const originOptions =
    originAttribute && originAttribute.values.length > 0
      ? originAttribute.values.map((v) => `${v.value} (${v.count})`)
      : defaultOriginOptions.map((value) => `${value} (0)`);

  return (
    <FilterSectionWithDropdown
      title="原產地"
      attributeId={originAttribute?.id || ORIGIN_ATTRIBUTE_ID}
      options={originOptions.map((i) => ({ label: i, value: i }))}
      expanded={expanded}
      onOptionSelect={(attrId, values) => {
        const newFilters = {
          ...filters,
          attrs: {
            ...filters.attrs,
            [attrId]: values.map(extractValue),
          },
        };
        setFilters(newFilters);
        onFilterUpdate(newFilters);
      }}
      selectedValues={
        filters.attrs[originAttribute?.id || ORIGIN_ATTRIBUTE_ID]?.map((value) => {
          const option = originAttribute?.values?.find((v) => v.value === value);
          return option ? `${value} (${option.count})` : `${value} (0)`;
        }) || []
      }
    />
  );
}

function FilterStorage({
  attributeOptions,
  filters,
  setFilters,
  onFilterUpdate,
  extractValue,
  expanded,
}: {
  attributeOptions: ApiAttributeOption[];
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  onFilterUpdate: (filters: FilterState) => void;
  extractValue: (optionWithCount: string) => string;
  expanded?: boolean;
}) {
  const groupttributeOptions = useGroupttributeOptions(attributeOptions);
  const storageAttribute = groupttributeOptions.find((attr) => attr.id === STORAGE_ATTRIBUTE_ID);

  if (storageAttribute) {
    storageAttribute.name = "儲存方法";
  }

  const storageOptions =
    storageAttribute && storageAttribute.values.length > 0
      ? storageAttribute.values.map((v) => `${v.value} (${v.count})`)
      : defaultStorageOptions.map((value) => `${value} (0)`);

  return (
    <FilterSectionWithDropdown
      title="儲存方法"
      attributeId={storageAttribute?.id || STORAGE_ATTRIBUTE_ID}
      options={storageOptions.map((i) => ({ label: i, value: i }))}
      expanded={expanded}
      onOptionSelect={(attrId, values) => {
        const newFilters = {
          ...filters,
          attrs: {
            ...filters.attrs,
            [attrId]: values.map(extractValue),
          },
        };
        setFilters(newFilters);
        onFilterUpdate(newFilters);
      }}
      selectedValues={
        filters.attrs[storageAttribute?.id || STORAGE_ATTRIBUTE_ID]?.map((value) => {
          const option = storageAttribute?.values?.find((v) => v.value === value);
          return option ? `${value} (${option.count})` : `${value} (0)`;
        }) || []
      }
    />
  );
}

function FilterBrand({
  brandOptions,
  filters,
  setFilters,
  onFilterUpdate,
  expanded,
}: {
  brandOptions: BrandOption[];
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  onFilterUpdate: (filters: FilterState) => void;
  expanded?: boolean;
}) {
  return (
    <FilterSectionWithDropdown
      title="品牌"
      attributeId="brand"
      options={brandOptions.map((b) => ({ label: `${b.name} (${b.count})`, value: b.id }))}
      expanded={expanded}
      onOptionSelect={(_, values) => {
        const newFilters = {
          ...filters,
          selectedBrands: values,
        };
        setFilters(newFilters);
        onFilterUpdate(newFilters);
      }}
      selectedValues={filters.selectedBrands.map((name) => {
        const brand = brandOptions.find((b) => b.name === name);
        return brand ? `${name} (${brand.count})` : name;
      })}
    />
  );
}
